/**
 * UserCountryContext.js
 *
 * Provides global access to the authenticated user's country object.
 * The country is sourced exclusively from the login response (user.country)
 * and MUST NOT be editable by the user.
 *
 * Shape: { id: number, name: string, flag: string, code: string } | null
 *
 * ── Security ────────────────────────────────────────────────────────────────
 * The country object is stored in localStorage together with an integrity
 * signature derived from the JWT signature segment (the third part of the
 * token, which is a server-computed HMAC that changes on every login).
 *
 * If an attacker edits `localStorage.userCountry` via DevTools, the stored
 * signature will no longer match → the read returns null → the plans page
 * shows "no country configured" instead of wrong prices.
 *
 * This does NOT prevent an attacker who also has the raw JWT from forging a
 * valid signature, but since possession of the JWT already grants full API
 * access, this represents the appropriate level of client-side protection.
 *
 * Usage:
 *   const { userCountry, setUserCountry } = useUserCountry();
 */
import React, { createContext, useContext, useState, useCallback } from "react";

const LS_KEY     = "userCountry";
const LS_SIG_KEY = "userCountry_sig";

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Extract the signature segment (3rd part) of the stored JWT. */
function getTokenSignature() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return "";
    return token.split(".")[2] || "";
  } catch {
    return "";
  }
}

/**
 * Compute a simple integrity tag.
 * Not cryptographically unbreakable, but prevents casual DevTools tampering.
 */
function computeSig(countryObj, tokenSig) {
  try {
    const raw = JSON.stringify(countryObj) + "|" + tokenSig;
    // btoa only handles latin1 — encode to UTF-8 bytes first via encodeURIComponent
    return btoa(
      encodeURIComponent(raw).replace(/%([0-9A-F]{2})/g, (_, p1) =>
        String.fromCharCode(parseInt(p1, 16))
      )
    );
  } catch {
    return "";
  }
}

function readFromStorage() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;

    const country    = JSON.parse(raw);
    const storedSig  = localStorage.getItem(LS_SIG_KEY);
    const tokenSig   = getTokenSignature();
    const expectedSig = computeSig(country, tokenSig);

    if (!storedSig || !tokenSig || storedSig !== expectedSig) {
      // Value was tampered with or the session changed — discard silently.
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(LS_SIG_KEY);
      return null;
    }

    return country;
  } catch {
    return null;
  }
}

function writeToStorage(country) {
  if (country) {
    const tokenSig = getTokenSignature();
    const sig      = computeSig(country, tokenSig);
    localStorage.setItem(LS_KEY,     JSON.stringify(country));
    localStorage.setItem(LS_SIG_KEY, sig);
  } else {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem(LS_SIG_KEY);
  }
}

// ── Context ──────────────────────────────────────────────────────────────────

const UserCountryContext = createContext(null);

export function UserCountryProvider({ children }) {
  const [userCountry, setUserCountryState] = useState(() => readFromStorage());

  /**
   * Persists the country from the login response.
   * Call this ONLY from the login flow — never expose it to user-facing UI.
   * Pass `null` to clear (e.g., on logout).
   */
  const setUserCountry = useCallback((country) => {
    writeToStorage(country);
    setUserCountryState(country);
  }, []);

  const value = React.useMemo(
    () => ({ userCountry, setUserCountry }),
    [userCountry, setUserCountry]
  );

  return (
    <UserCountryContext.Provider value={value}>
      {children}
    </UserCountryContext.Provider>
  );
}

export function useUserCountry() {
  const ctx = useContext(UserCountryContext);
  if (!ctx) {
    throw new Error("useUserCountry must be used inside <UserCountryProvider>");
  }
  return ctx;
}
